/**
 * This file tracks events specific to product.
 */

import {type Zoom} from 'src/components/PanelMediaBrowser/controls/ZoomControl';

import {type ZoomSource} from '../../components/PanelMediaBrowser/modal/types';
import {envIsServerDeployment} from '../../config';
import {type SmoothingType} from '../../generated/graphql';
import {makeTrackEvent} from './makeTrackEvent';
import {type ActivationMethods, type NoEventData} from './types';

export const enum ProdEventNames {
  AppLauncherFailure = 'app-launcher-failure', // tracks non-executable app launcher attempts
  AppLauncherUsed = 'app-launcher-used', // tracks each time the app launcher is used to navigate
  AppLauncherOnScreen = 'app-launcher-on-screen', // tracks each time the app launcher is triggered
  RunGroupNodeViewed = 'Run Group Node Viewed', // tracks each time a user views a run group node in the DAG
  SearchNavOnScreen = 'search-nav-on-screen', // tracks each time the search nav is triggered
  SearchNavUsed = 'search-nav-used', // tracks each time the search nav is used to navigate
  SmoothingChanged = 'Smoothing changed', // tracks each time smoothing is applied to a plot
  TempViewNavigation = 'Temporary Workspace View Bar Navigation Triggered', // tracks each time the user navigates to a temp view
  MediaSliderMetricKeySelected = 'Media slider metric key selected', // tracks each time a user selects a metric key other than _step to navigate through a media panel
  MediaSliderMetricKeyRemoved = 'Media slider metric key removed', // tracks each time a user selects the default, _step, metric after previously selecting a different metric
  MediaFullscreenModalOpened = 'Media fullscreen modal opened', // tracks each time a user opens the media fullscreen modal
  MediaVideoSyncEnabled = 'Media video sync enabled', // tracks each time a user enables video sync
  MediaVideoSyncDisabled = 'Media video sync disabled', // tracks each time a user disables video sync
  MediaFullscreenModalClosed = 'Media fullscreen modal closed', // tracks each time a user closes the media fullscreen modal and the length of time it was open in milliseconds
  MediaFullscreenModalImageZoom = 'Media fullscreen modal image zoom', // tracks each time a user zooms the image in the media fullscreen modal
  MediaFullscreenModalImageZoomControlClick = 'Media fullscreen modal image zoom control click', // tracks each time a user clicks the zoom control in the media fullscreen modal
  MediaFullscreenModalImageClickAndDrag = 'Media fullscreen modal image click and drag', // tracks each time a user clicks and drags the image in the media fullscreen modal
  MediaFullscreenModalImageScroll = 'Media fullscreen modal image scroll', // tracks each time a user scrolls the image in the media fullscreen modal
  MediaCompareModeActivated = 'Media compare mode activated', // tracks each time a user activates media compare mode and where they entered from
  HomepageRunNavigation = 'Homepage Run Navigation', // tracks each time a user navigates to a run from the homepage activity surface
  NavResourcesMissingServerVersion = 'nav-resources-missing-server-version', // tracks when /api/server-version returns a value that doesn't match semver
  NavResourcesServerVersionFetchError = 'nav-resources-server-version-fetch-error', // tracks when the /api/server-version fetch throws
  NavRightFeatureOpened = 'nav-right-feature-opened', // tracks when a user opens a feature in the right side of the top nav bar
  KeyboardShortcutUsed = 'keyboard-shortcut-used', // tracks when a user triggers a registered keyboard shortcut
  KeyboardShortcutFailed = 'keyboard-shortcut-failed', // tracks when a registered keyboard shortcut throws an error
  RunOverviewSdkVersionFetchError = 'run-overview-sdk-version-fetch-error', // tracks when the pypi fetch for the newer-sdk tag fails
  MarimoViewStaticExport = 'marimo-view-static-export', // tracks when a user views the static export of a marimo notebook
  MarimoClickRun = 'marimo-click-run', // tracks when a user clicks run in the marimo panel
  MarimoClickViewNotebook = 'marimo-click-view-notebook', // tracks when a user clicks view notebook in the marimo panel
  MarimoCreateNotebookPanelSettings = 'marimo-create-notebook-panel-settings', // tracks when a user creates a new marimo notebook in the marimo panel settings
  MarimoForkNotebookPanelSettings = 'marimo-fork-notebook-panel-settings', // tracks when a user forks a marimo notebook in the marimo panel settings
  MediaPanelSnapFromChartHotkey = 'media_panel_snap_from_chart_hotkey', // tracks when a user snaps the media panel to a step from the line chart hotkey
}

export type QuickNavData = {
  activationMethod: ActivationMethods;
  destination: string;
  location: string;
  type: 'option-selected' | 'direct-navigation';
};

type KeyboardShortcutEventData = {
  currentScope: string;
  shortcutId: string;
  shortcutName: string;
};

type KeyboardShortcutFailedEventData = KeyboardShortcutEventData & {
  errorMessage: string;
};

export type QuickNavDescriptions = {
  activationMethod: {
    description: 'Whether the action was triggered by keyboard shortcut or mouse';
    exampleValues: [
      ActivationMethods.MouseClick,
      ActivationMethods.KeyboardHotkey,
    ];
  };
  destination: {
    description: 'The new app location being navigated to';
    exampleValues: ['/home', '/[entityName]'];
  };
  location: {
    description: 'The current location within the application';
    exampleValues: ['/home', '/[entityName]'];
  };
  type: {
    description: 'This determines if a user found a matching option in the results list or if they successfully navigated to a location by typing the URL directly';
    exampleValues: ['option-selected', 'direct-navigation'];
  };
};

export const withinLastFilterAdded = makeTrackEvent<
  NoEventData,
  {
    _description: 'Fires once when the user adds the "within last" op to a filter';
    _location: 'Workspace > edit filters UI';
    _motivation: "We want to track usage of this new feature we've added";
  }
>('User added Within Last Filter');
export const withinLastFilterRemoved = makeTrackEvent<
  NoEventData,
  {
    _description: 'Fires once when the user removes the "within last" op from a filter';
    _location: 'Workspace > edit filters UI';
    _motivation: "We want to track usage of this new feature we've added";
  }
>('User removed Within Last Filter');

export const appLauncherFailure = makeTrackEvent<
  Pick<QuickNavData, 'destination' | 'location'>,
  QuickNavDescriptions & {
    _description: 'A user has tried to enter a search term in the launcher nav that does not link to a destination';
    _location: 'Global - App launcher bar';
    _motivation: 'Identify destinations users desire but we do not support';
  }
>(ProdEventNames.AppLauncherFailure);

export const appLauncherUsed = makeTrackEvent<
  Omit<QuickNavData, 'activationMethod'>,
  QuickNavDescriptions & {
    _description: 'A successful entry in the app launcher leading to a new destination';
    _location: 'Global - App launcher bar';
    _motivation: 'Identify where users quickly navigate to';
  }
>(ProdEventNames.AppLauncherUsed);

export const appLauncherOnScreen = makeTrackEvent<
  Pick<QuickNavData, 'activationMethod' | 'location'>,
  QuickNavDescriptions & {
    _description: 'The app launcher widget has become visible in the app';
    _location: 'Global - App launcher bar';
    _motivation: 'Track usage / ease of discovery';
  }
>(ProdEventNames.AppLauncherOnScreen);

export const searchNavOnScreen = makeTrackEvent<
  Pick<QuickNavData, 'activationMethod' | 'location'>,
  QuickNavDescriptions & {
    _description: 'The search nav dropdown is being used';
    _location: 'Global - Header nav search input';
    _motivation: 'Track usage of search in header nav';
  }
>(ProdEventNames.SearchNavOnScreen);

export const searchNavUsed = makeTrackEvent<
  Pick<QuickNavData, 'destination' | 'location'>,
  QuickNavDescriptions & {
    _description: 'A successful entry in the search nav leading to a new destination';
    _location: 'Global - Header nav search input';
    _motivation: 'Identify where users quickly navigate to';
  }
>(ProdEventNames.SearchNavUsed);

export const keyboardShortcutUsed = makeTrackEvent<
  KeyboardShortcutEventData,
  {
    currentScope: {
      description: 'The active input system scope when the shortcut was triggered';
      exampleValues: ['global', 'workspace'];
    };
    shortcutId: {
      description: 'The stable id of the keyboard shortcut action';
      exampleValues: ['view-keyboard-shortcuts', 'minimize-run-selector'];
    };
    shortcutName: {
      description: 'The human-readable keyboard shortcut action name';
      exampleValues: [
        'View keyboard shortcuts',
        'Toggle minimized run selector',
      ];
    };
    _description: 'A user triggered a keyboard shortcut registered in the input system';
    _location: 'Global - InputSystemProvider';
    _motivation: 'Understand keyboard shortcut usage and prioritization';
  }
>(ProdEventNames.KeyboardShortcutUsed);

export const keyboardShortcutFailed = makeTrackEvent<
  KeyboardShortcutFailedEventData,
  {
    currentScope: {
      description: 'The active input system scope when the shortcut was triggered';
      exampleValues: ['global', 'workspace'];
    };
    shortcutId: {
      description: 'The stable id of the keyboard shortcut action';
      exampleValues: ['view-keyboard-shortcuts', 'minimize-run-selector'];
    };
    shortcutName: {
      description: 'The human-readable keyboard shortcut action name';
      exampleValues: [
        'View keyboard shortcuts',
        'Toggle minimized run selector',
      ];
    };
    errorMessage: {
      description: 'The error message thrown by the keyboard shortcut handler';
      exampleValues: ['Cannot read properties of undefined'];
    };
    _description: 'A registered keyboard shortcut handler threw an error';
    _location: 'Global - InputSystemProvider';
    _motivation: 'Track keyboard shortcut failures for debugging and reliability';
  }
>(ProdEventNames.KeyboardShortcutFailed);

export const pointCloudViewed = makeTrackEvent<
  {
    type: 'fullscreen' | 'preview';
  },
  {
    type: {
      description: 'is the point cloud being viewed inside of the workspace panel as a preview, or has the user clicked to view the pointcloud fullscreen?';
      exampleValues: ['fullscreen', 'preview'];
    };
  } & {
    _description: 'fired for each instance of a point cloud - eg if 5 point cloud previews are rendered (visible in any part of html then this will fire 5 times. Full screen will fire once per time the user clicks to open full screen mode';
    _location: 'Workspaces > Media Panel > Point Cloud viewer';
    _motivation: 'How many folks are using the point cloud viewer?';
  }
>('3D Point Cloud Viewed');

export const pointCloudFileWorkaroundUsed = makeTrackEvent<
  NoEventData,
  {
    _description: 'There was a bug in the way the SDK was submitting files, this tracks when we have to use the workaround to view those files correctly. See detailed discussion in https://github.com/wandb/wandb/pull/4353';
    _location: 'Object3DCard';
    _motivation: 'Do we even need to keep this workaround in the code?';
  }
>('3D Point Cloud Viewed, required workaround');

type SmoothingEventData = {
  entityName: string;
  panelId?: string;
  projectName: string;
  smoothingType: SmoothingType;
};

export const smoothingChanged = makeTrackEvent<
  SmoothingEventData,
  {
    _description: 'A user has changed the smoothing settings in a chart';
    _location: 'A ';
    _motivation: 'Track usage of this feature';
    entityName: {
      description: 'The entity the project belongs to';
      exampleValues: ['my-org', 'my-user'];
    };
    panelId: {
      description: 'The panel ID if';
      exampleValues: ['', ''];
    };
    projectName: {
      description: 'The project being viewed';
      exampleValues: ['my-project'];
    };
    smoothingType: {
      description: 'The smoothing algorithm being applied';
      exampleValues: [
        SmoothingType.None,
        SmoothingType.Gaussian,
        SmoothingType.ExponentialTimeWeighted,
      ];
    };
  }
>(ProdEventNames.SmoothingChanged);

export const viewRunGroupNode = makeTrackEvent<
  {
    entityName: string;
    projectName: string;
    artifactCollectionName: string;
    artifactCommitHash: string;
    artifactTab: string;
    artifactTypeName: string;
    numRuns: number;
  },
  {
    _description: 'A user has clicked on a run group node in DAG view';
    _location: 'Simple lineage view in the artifact DAG';
    _motivation: 'Track usage of this feature';
    entityName: {
      description: 'The entity currently viewing the workspace';
      exampleValues: ['my-org', 'my-user'];
    };
    projectName: {
      description: 'The project being viewed';
      exampleValues: ['my-project'];
    };
    artifactCollectionName: {
      description: 'The artifact being viewed';
      exampleValues: ['MNIST_test'];
    };
    artifactCommitHash: {
      description: 'The hash of the artifact';
      exampleValues: ['v0'];
    };
    artifactTab: {
      description: 'The active tab in the artifact view';
      exampleValues: ['lineage'];
    };
    artifactTypeName: {
      description: 'The type of artifact being viewed';
      exampleValues: ['dataset'];
    };
    numRuns: {
      description: 'The number of runs in the run group';
      exampleValues: [5];
    };
  }
>(ProdEventNames.RunGroupNodeViewed);

export const trackPlotKeyboardCopy = makeTrackEvent<
  NoEventData,
  {
    _description: 'This event fires when the user is looking at a plot, has hovered over the plot and presses the key combo to copy the contents of the legeng to the clipboard';
    _location: 'Anywhere plot panel can be rendered - Workspaces/Reports usually';
    _motivation: 'We created this feature and want to understand if anyone is using it';
  }
>('Plot legend data copied to clipboard');

export const trackMinMaxAggregation = makeTrackEvent<
  {
    type: 'latest' | 'min' | 'max';
  },
  {
    type: {
      description: 'the aggregation viewed by the user';
      exampleValues: ['latest', 'min', 'max'];
    };
  } & {
    _description: 'This event fires when the user selects an option in the aggregation dropdown';
    _location: 'In the dropdown for a column in the runs table';
    _motivation: 'Understand how useful this feature is to users';
  }
>('Min/Max aggregation selected');

export const trackAddedPanelGridToReport = makeTrackEvent<
  NoEventData,
  {
    _description: 'Fires when a panelgrid is added to a report.';
    _location: 'Multiple, including: Project page, report page, anywhere create report prompt can be shown';
    _motivation: 'Fullstory cue - understand how reports are being used';
  }
>('Runset panel grid added to report');

export const trackAddPanelMenuClosed = makeTrackEvent<
  NoEventData,
  {
    _description: 'Fires after the user has clicked the Add Panel button, selected item, and the menu is now closing';
    _location: 'report page, panel grid action buttons';
    _motivation: 'Fullstory cue - understand how reports are being used';
  }
>('Add Panel Menu Closed');

export const trackImportPanelMenuClosed = makeTrackEvent<
  NoEventData,
  {
    _description: 'Fires after the user has clicked the Import Panel button, selected a project+panel, and the menu is now closing';
    _location: 'report page, panel grid action buttons';
    _motivation: 'Fullstory cue - understand how reports are being used';
  }
>(`Import Panel Menu Closed`);

export const trackAddRunSetToPanelGrid = makeTrackEvent<
  NoEventData,
  {
    _description: 'Fires after the user has clicked the + button to add a run set to the panel grid';
    _location: 'report page, panel grid';
    _motivation: 'Fullstory cue - understand how reports are being used';
  }
>('Add Run Set Clicked');

export const trackWorkspaceSettingsOpened = makeTrackEvent<
  {
    entityName: string;
    projectName: string;
    workspaceID: string;
    user: string;
  },
  {
    entityName: {
      description: 'The entity the project belongs to';
      exampleValues: ['fellowshipAI'];
    };
    projectName: {
      description: 'The project being viewed';
      exampleValues: ['destroy-ring'];
    };
    workspaceID: {
      description: 'The workspace being viewed';
      exampleValues: ['workspace-6k4mybvm2'];
    };
    user: {
      description: 'The user viewing the workspace';
      exampleValues: ['bilbobaggins'];
    };
  } & {
    _description: 'User opened workspace settings modal';
    _location: 'workspaces panelbank';
    _motivation: 'see usage of existing feature';
  }
>('Workspace settings modal viewed');

export const trackPanelSearchHistoryOpened = makeTrackEvent<
  NoEventData,
  {
    _description: 'When the panel search history button is clicked, we show a dropdown menu - this tracks when that occurs';
    _location: 'workspaces panelbank';
    _motivation: 'new UI added, track usage';
  }
>('Panel search history menu opened');

export const trackPanelSearchCleared = makeTrackEvent<
  NoEventData,
  {
    _description: 'When the user clicks the clear button located inside of the panel search box';
    _location: 'workspaces panelbank';
    _motivation: 'new UI added, track usage';
  }
>('Panel search cleared');

export const trackPanelSearchHistoryItemSelected = makeTrackEvent<
  {index: number},
  {
    _description: 'When the user clicks an item in the panel search history dropdown';
    _location: 'workspaces panelbank';
    _motivation: 'new UI added, track usage';
    index: {
      description: 'The zero-based index of the item in the list that was clicked by the user';
      exampleValues: [0, 10];
    };
  }
>('Panel search history item selected');

export const trackPanelSearchAutoExpandToggled = makeTrackEvent<
  {value: boolean},
  {
    _description: 'User updated the auto-expand setting during panel search';
    _location: 'workspaces panelbank';
    _motivation: 'new UI added, track usage';
    value: {
      description: 'Whether auto-expand is on or off';
      exampleValues: [true, false];
    };
  }
>('Panel search auto-expand toggled');

export const mediaSliderMetricKeySelected = makeTrackEvent<
  {
    metricKey: string;
    entityName: string;
    projectName: string;
    user?: string;
    panelId?: string;
  },
  {
    _description: 'Fired when a user selects a metric key other than _step to navigate through a media panel';
    _location: 'Workspaces > Media Panel > Settings';
    _motivation: 'How many folks are using a metric other than step to navigate through a media panel?';
    metricKey: {
      description: 'The metric key that was selected';
      exampleValues: ['epoch', 'checkpoint'];
    };
    entityName: {
      description: 'The entity the project belongs to';
      exampleValues: ['my-org', 'my-user'];
    };
    projectName: {
      description: 'The project being viewed';
      exampleValues: ['my-project'];
    };
    user: {
      description: 'The user viewing the workspace';
      exampleValues: ['bilbobaggins'];
    };
    panelId: {
      description: 'The panel ID if set in query parameters';
      exampleValues: ['', ''];
    };
  }
>(ProdEventNames.MediaSliderMetricKeySelected);

export const mediaSliderMetricKeyRemoved = makeTrackEvent<
  {
    previousMetricKey: string;
    entityName: string;
    projectName: string;
    user?: string;
    panelId?: string;
  },
  {
    _description: 'Fired when a user selects the default, _step, metric after previously selecting a different metric';
    _location: 'Workspaces > Media Panel > Settings';
    _motivation: 'How many users opt to go back to navigating by step after selecting another metric?';
    previousMetricKey: {
      description: 'The metric key that was removed';
      exampleValues: ['epoch', 'checkpoint'];
    };
    entityName: {
      description: 'The entity the project belongs to';
      exampleValues: ['my-org', 'my-user'];
    };
    projectName: {
      description: 'The project being viewed';
      exampleValues: ['my-project'];
    };
    user: {
      description: 'The user viewing the workspace';
      exampleValues: ['bilbobaggins'];
    };
    panelId: {
      description: 'The panel ID if set in query parameters';
      exampleValues: ['', ''];
    };
  }
>(ProdEventNames.MediaSliderMetricKeyRemoved);

export const trackAdminTool = makeTrackEvent<
  {
    action: 'Pro plan upgrade' | 'Plan upgrade';
    orgId: string;
    newPlan: string;
    seats: number | null;
    weaveIngestionTier?: string;
    previousPlan?: string;
  },
  {
    action: {
      description: 'The specific action taken within the AdminTooling event';
      exampleValues: ['Pro plan upgrade', 'Plan upgrade'];
    };
    orgId: {
      description: 'The ID of the organization';
      exampleValues: ['org-123'];
    };
    newPlan: {
      description: 'The new plan name';
      exampleValues: ['Pro Annual', 'Enterprise'];
    };
    seats: {
      description: 'The number of seats in the new plan';
      exampleValues: [10, 4];
    };
    weaveIngestionTier: {
      description: 'The weave ingestion tier, if applicable';
      exampleValues: ['1500', '3000'];
    };
    previousPlan: {
      description: 'Plan name of previous plan';
      exampleValues: ['Pro Trial', 'Enterprise'];
    };
    _description: 'Tracks actions related to admin tooling, such as plan upgrades';
    _location: 'Admin panel > Subscription management';
    _motivation: 'Track admin actions for subscription management';
  }
>('AdminTooling');

export const mediaFullscreenModalOpened = makeTrackEvent<
  {
    metricKey: string;
    entityName: string;
    projectName: string;
    user?: string;
    panelId?: string;
  },
  {
    _description: 'Fired when a user opens the media fullscreen modal';
    _location: 'Workspaces > Media Panel > Fullscreen Modal';
    _motivation: 'How many folks are opening the media fullscreen modal?';
    metricKey: {
      description: 'The media key that was opened';
      exampleValues: ['epoch', 'checkpoint'];
    };
    entityName: {
      description: 'The entity the project belongs to';
      exampleValues: ['my-org', 'my-user'];
    };
    projectName: {
      description: 'The project being viewed';
      exampleValues: ['my-project'];
    };
    user: {
      description: 'The user viewing the workspace';
      exampleValues: ['bilbobaggins'];
    };
    panelId: {
      description: 'The panel ID if set in query parameters';
      exampleValues: ['', ''];
    };
  }
>(ProdEventNames.MediaFullscreenModalOpened);

export const mediaFullscreenModalClosed = makeTrackEvent<
  {
    metricKey?: string;
    entityName?: string;
    projectName?: string;
    user?: string;
    panelId?: string;
    duration: number;
    startTime: number;
  },
  {
    _description: 'Fired when a user closes the media fullscreen modal';
    _location: 'Workspaces > Media Panel > Fullscreen Modal';
    _motivation: 'How many folks are closing the media fullscreen modal and how long are they spending in it?';
    metricKey: {
      description: 'The media key that was opened';
      exampleValues: ['epoch', 'checkpoint'];
    };
    entityName: {
      description: 'The entity the project belongs to';
      exampleValues: ['my-org', 'my-user'];
    };
    projectName: {
      description: 'The project being viewed';
      exampleValues: ['my-project'];
    };
    user: {
      description: 'The user viewing the workspace';
      exampleValues: ['bilbobaggins'];
    };
    panelId: {
      description: 'The panel ID if set in query parameters';
      exampleValues: ['', ''];
    };
    duration: {
      description: 'The duration of the media fullscreen modal being open in milliseconds';
      exampleValues: [1000];
    };
    startTime: {
      description: 'The start time of the media fullscreen modal being open in unixtime milliseconds (i.e. Date.now())';
      exampleValues: [1000];
    };
  }
>(ProdEventNames.MediaFullscreenModalClosed);

export const mediaFullscreenModalImageZoom = makeTrackEvent<
  {
    prevZoom: Zoom;
    newZoom: Zoom;
    zoomToFitPercentage: number | null;
    source: ZoomSource;
  },
  {
    _description: 'Fired when a user zooms the image in the media fullscreen modal';
    _location: 'Workspaces > Media Panel > Fullscreen Modal';
    _motivation: 'How many folks are zooming the image in the media fullscreen modal?';
    prevZoom: {
      description: 'The previous zoom level of the image as a percentage, or "fit" if the image was zoomed to fit';
      exampleValues: [90, 'fit'];
    };
    newZoom: {
      description: 'The new zoom level of the image as a percentage, or "fit" if the image was zoomed to fit';
      exampleValues: [100, 'fit'];
    };
    zoomToFitPercentage: {
      description: 'The zoom level of the image as a percentage when zoomed to fit';
      exampleValues: [100];
    };
    source: {
      description: 'The source of the zoom action';
      exampleValues: [ZoomSource.Keyboard, ZoomSource.Dropdown];
    };
  }
>(ProdEventNames.MediaFullscreenModalImageZoom);

export type MediaVideoSyncEventData = {
  entityName: string;
  projectName: string;
  level?: 'workspace' | 'section' | 'panel';
};

type MediaVideoSyncEventDescriptions = {
  entityName: {
    description: 'The entity the project belongs to';
    exampleValues: ['my-org', 'my-user'];
  };
  projectName: {
    description: 'The project being viewed';
    exampleValues: ['my-project'];
  };
  level: {
    description: 'The level of the setting';
    exampleValues: ['workspace', 'section', 'panel'];
  };
};

export const mediaVideoSyncEnabled = makeTrackEvent<
  MediaVideoSyncEventData,
  MediaVideoSyncEventDescriptions & {
    _description: 'Fired when a user enables video sync';
    _location: 'Workspaces > Media Panel > Settings';
    _motivation: 'How many folks are enabling video sync?';
  }
>(ProdEventNames.MediaVideoSyncEnabled);

export const mediaVideoSyncDisabled = makeTrackEvent<
  MediaVideoSyncEventData,
  MediaVideoSyncEventDescriptions & {
    _description: 'Fired when a user disables video sync';
    _location: 'Workspaces > Media Panel > Settings';
    _motivation: 'How many folks are disabling video sync?';
  }
>(ProdEventNames.MediaVideoSyncDisabled);

export const mediaFullscreenModalImageZoomControlClick = makeTrackEvent<
  {
    isOpen: boolean;
  },
  {
    _description: 'Fired when a user clicks the zoom control in the media fullscreen modal';
    _location: 'Workspaces > Media Panel > Fullscreen Modal';
    _motivation: 'How many folks are clicking the zoom control in the media fullscreen modal?';
    isOpen: {
      description: 'Whether the zoom control dropdown is now open or closed';
      exampleValues: [true, false];
    };
  }
>(ProdEventNames.MediaFullscreenModalImageZoomControlClick);

export const mediaFullscreenModalImageClickAndDrag = makeTrackEvent<
  {
    movementX: number;
    movementY: number;
    startX: number;
    startY: number;
    endX: number;
    endY: number;
  },
  {
    _description: 'Fired when a user clicks and drags the image in the media fullscreen modal';
    _location: 'Workspaces > Media Panel > Fullscreen Modal';
    _motivation: 'How many folks are clicking and dragging the image in the media fullscreen modal?';
    movementX: {
      description: 'The horizontal movement of the mouse';
      exampleValues: [100];
    };
    movementY: {
      description: 'The vertical movement of the mouse';
      exampleValues: [100];
    };
    startX: {
      description: 'The starting x position of the mouse';
      exampleValues: [100];
    };
    startY: {
      description: 'The starting y position of the mouse';
      exampleValues: [100];
    };
    endX: {
      description: 'The ending x position of the mouse';
      exampleValues: [100];
    };
    endY: {
      description: 'The ending y position of the mouse';
      exampleValues: [100];
    };
  }
>(ProdEventNames.MediaFullscreenModalImageClickAndDrag);

export const mediaFullscreenModalImageScroll = makeTrackEvent<
  {
    scrollLeft: number;
    scrollTop: number;
    prevViewportCenterPercentX: number;
    prevViewportCenterPercentY: number;
    newViewportCenterPercentX: number;
    newViewportCenterPercentY: number;
  },
  {
    _description: 'Fired when a user scrolls the image in the media fullscreen modal';
    _location: 'Workspaces > Media Panel > Fullscreen Modal';
    _motivation: 'How many folks are scrolling the image in the media fullscreen modal?';
    scrollLeft: {
      description: 'The horizontal scroll position of the image';
      exampleValues: [100];
    };
    scrollTop: {
      description: 'The vertical scroll position of the image';
      exampleValues: [100];
    };
    prevViewportCenterPercentX: {
      description: 'The previous x position of the viewport center as a percentage';
      exampleValues: [100];
    };
    prevViewportCenterPercentY: {
      description: 'The previous y position of the viewport center as a percentage';
      exampleValues: [100];
    };
    newViewportCenterPercentX: {
      description: 'The new x position of the viewport center as a percentage';
      exampleValues: [100];
    };
    newViewportCenterPercentY: {
      description: 'The new y position of the viewport center as a percentage';
      exampleValues: [100];
    };
  }
>(ProdEventNames.MediaFullscreenModalImageScroll);

export type MediaCompareModeEntryPoint =
  | 'workspace_panel'
  | 'fullscreen_panel'
  | 'fullscreen_modal';

export const mediaCompareModeActivated = makeTrackEvent<
  {
    entryPoint: MediaCompareModeEntryPoint;
    previousMode?: 'gallery' | 'grid' | 'compare';
  },
  {
    _description: 'Fired when a user enters media compare mode';
    _location: 'Workspaces > Media Panel';
    _motivation: 'Measure compare-mode adoption and which entry points drive activation';
    entryPoint: {
      description: 'Where the user activated compare mode';
      exampleValues: [
        'workspace_panel',
        'fullscreen_panel',
        'fullscreen_modal',
      ];
    };
    previousMode: {
      description: 'The panel mode before compare mode was activated';
      exampleValues: ['gallery', 'grid'];
    };
  }
>(ProdEventNames.MediaCompareModeActivated);

export const trackNavResourcesMissingServerVersion = makeTrackEvent<
  {response: string},
  {
    _description: 'Fires when /api/server-version returns a value that does not match semver, causing the release notes link to fall back to the static URL';
    _location: 'Nav > Resources & help menu';
    _motivation: 'Identify server deployments returning an unexpected SERVER_RELEASE_VERSION format';
    response: {
      description: 'The raw value returned by /api/server-version that failed the semver check';
      exampleValues: ['', 'v0.79.0', 'latest'];
    };
  }
>(ProdEventNames.NavResourcesMissingServerVersion);

type NavRightFeature =
  | 'search'
  | 'alerts'
  | 'run_queue'
  | 'whats_new'
  | 'resources'
  | 'agent'
  | 'invite_teammates';

const _trackNavRightFeatureOpened = makeTrackEvent<
  {
    feature: NavRightFeature;
    org_id: string | null;
    is_admin_mode: boolean;
  },
  {
    _description: 'Fires when a user opens a feature in the right side of the top navigation bar';
    _location: 'Global - Top navigation bar (right side)';
    _motivation: 'Establish baseline usage metrics for each nav bar feature to enable comparison and future analyses';
    feature: {
      description: 'The nav bar feature the user engaged';
      exampleValues: [
        'search',
        'alerts',
        'run_queue',
        'whats_new',
        'resources',
        'agent',
        'invite_teammates',
      ];
    };
    org_id: {
      description: 'The ID of the currently selected organization account; null for personal accounts';
      exampleValues: ['org-abc123', null];
    };
    is_admin_mode: {
      description: 'Whether the viewer has W&B admin mode active';
      exampleValues: [true, false];
    };
  }
>(ProdEventNames.NavRightFeatureOpened, {isEnabled: !envIsServerDeployment});

// Wrapper around _trackNavRightFeatureOpened so callers can pass selected_account
// directly rather than computing org_id themselves. makeTrackEvent passes data
// straight to Analytics.track with no transform step, so the derivation has to
// live here instead.
export const trackNavRightFeatureOpened = ({
  feature,
  selected_account,
  is_admin_mode,
}: {
  feature: NavRightFeature;
  selected_account: {accountType: string; id?: string} | null | undefined;
  is_admin_mode: boolean;
}) => {
  _trackNavRightFeatureOpened({
    feature,
    org_id:
      selected_account?.accountType === 'Personal'
        ? null
        : (selected_account?.id ?? null),
    is_admin_mode,
  });
};

export const trackNavResourcesServerVersionFetchError = makeTrackEvent<
  {error: string},
  {
    _description: 'Fires when the fetch to /api/server-version throws, causing the release notes link to fall back to the static URL';
    _location: 'Nav > Resources & help menu';
    _motivation: 'Identify server deployments where the version API is unreachable';
    error: {
      description: 'The error message from the failed fetch';
      exampleValues: ['Failed to fetch', 'NetworkError'];
    };
  }
>(ProdEventNames.NavResourcesServerVersionFetchError);

export const trackRunOverviewSdkVersionFetchError = makeTrackEvent<
  {error: string},
  {
    _description: 'Fires when the pypi fetch for the newer-sdk tag fails, causing the tag to be suppressed on the run overview';
    _location: 'Run overview > W&B CLI Version row';
    _motivation: 'Detect upstream pypi outages or schema drift that silently hide the newer-version nudge';
    error: {
      description: 'The error message from the failed fetch or schema validation';
      exampleValues: ['Failed to fetch', 'pypi fetch failed: 503'];
    };
  }
>(ProdEventNames.RunOverviewSdkVersionFetchError);

type MarimoNotebookEventData = {
  notebookId: string;
};

export type MarimoPanelSource = 'header' | 'panel-settings';

type MarimoNotebookEventDescriptions = {
  notebookId: {
    description: 'The ID of the marimo notebook associated with the panel action';
    exampleValues: ['notebook-abc123'];
  };
};

type MarimoPanelSourceDescriptions = {
  source: {
    description: 'Where in the marimo panel UI the user triggered the action';
    exampleValues: ['header', 'panel-settings'];
  };
};

export const trackMarimoViewStaticExport = makeTrackEvent<
  MarimoNotebookEventData,
  MarimoNotebookEventDescriptions & {
    _description: 'Fires when a user views the static export of a marimo notebook';
    _location: 'Workspaces > Marimo panel';
    _motivation: 'Track how often users view static notebook output vs running interactively';
  }
>(ProdEventNames.MarimoViewStaticExport);

export const trackMarimoClickRun = makeTrackEvent<
  MarimoNotebookEventData & {source: MarimoPanelSource},
  MarimoNotebookEventDescriptions &
    MarimoPanelSourceDescriptions & {
      _description: 'Fires when a user clicks run in the marimo panel';
      _location: 'Workspaces > Marimo panel';
      _motivation: 'Track usage of run and compare panel settings vs header entry points';
    }
>(ProdEventNames.MarimoClickRun);

export const trackMarimoClickViewNotebook = makeTrackEvent<
  MarimoNotebookEventData & {source: MarimoPanelSource},
  MarimoNotebookEventDescriptions &
    MarimoPanelSourceDescriptions & {
      _description: 'Fires when a user clicks view notebook in the marimo panel';
      _location: 'Workspaces > Marimo panel';
      _motivation: 'Track usage of view source and compare panel settings vs header entry points';
    }
>(ProdEventNames.MarimoClickViewNotebook);

export const trackMarimoCreateNotebookPanelSettings = makeTrackEvent<
  NoEventData,
  MarimoNotebookEventDescriptions & {
    _description: 'Fires when a user creates a new marimo notebook in the marimo panel settings';
    _location: 'Workspaces > Marimo panel settings';
    _motivation: 'Track how often users create new notebooks from the panel settings flow';
  }
>(ProdEventNames.MarimoCreateNotebookPanelSettings);

export const trackMarimoForkNotebookPanelSettings = makeTrackEvent<
  MarimoNotebookEventData,
  MarimoNotebookEventDescriptions & {
    _description: 'Fires when a user forks a marimo notebook in the marimo panel settings';
    _location: 'Workspaces > Marimo panel settings';
    _motivation: 'Track how often users fork existing notebooks from the panel settings flow';
  }
>(ProdEventNames.MarimoForkNotebookPanelSettings);

export const trackMediaPanelSnapFromChartHotkey = makeTrackEvent<
  {xAxis: string},
  {
    _description: 'Fired when a user snaps the media panel to a step from the line chart hotkey';
    _location: 'Workspaces > Line Plot';
    _motivation: 'How many users are snapping the media panel to a step from the line chart hotkey?';
    xAxis: {
      description: 'The x axis that was snapped to';
      exampleValues: ['epoch', 'checkpoint'];
    };
  }
>(ProdEventNames.MediaPanelSnapFromChartHotkey);
